import React, { FC } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@/components/extra/Icon/Icon';
import type { AnimationModeConfig } from '../AnimationTypes';

interface QualitySelection {
    value: string;
    id: string;
    label: string;
}

interface AnimationControlSecondaryProps {
    uniqueId: string | number;
    activePlaybackMode: AnimationModeConfig;
    qualitySelections: QualitySelection[];
    alignmentLabels?: string[];
    defaultAlignmentIndex?: number;
}

const AnimationControlSecondary: FC<AnimationControlSecondaryProps> = ({
    uniqueId,
    activePlaybackMode,
    qualitySelections,
    alignmentLabels = [],
    defaultAlignmentIndex = 0,
}) => {
    const hasMultipleFocusOptions = alignmentLabels.length > 1;

    return (
        <div className="Animation_controlGroup Animation_controlGroup--right">
            {/* Desktop Widget: Fokus */}
            {hasMultipleFocusOptions && (
                <div className="Animation_interactiveWrap Animation_desktopOnly">
                    <button
                        id={`FocusCtrl_${uniqueId}`}
                        className="Animation_btnView Animation_btnView--combo"
                    >
                        <Icon icon="alignment" />
                        <span className="Animation_btnText">
                            {alignmentLabels[defaultAlignmentIndex] ||
                                alignmentLabels[0]}
                        </span>
                    </button>
                    <div className="Animation_hudTooltip">
                        Fokus (Ausrichtung)
                    </div>
                    <div
                        id={`FocusPopup_${uniqueId}`}
                        className="Animation_popup"
                    >
                        {alignmentLabels.map((label, idx) => (
                            <div
                                key={`${label}_${idx}`}
                                className={`Animation_popupOption ${
                                    idx === defaultAlignmentIndex
                                        ? 'isActive'
                                        : ''
                                }`}
                                data-value={idx}
                            >
                                {label}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Desktop Widget: Speed */}
            <div className="Animation_interactiveWrap Animation_desktopOnly">
                <button
                    id={`FpsCtrl_${uniqueId}`}
                    className="Animation_btnView Animation_btnView--combo"
                >
                    <Icon icon="fps" />
                    <span className="Animation_btnText">
                        {activePlaybackMode.defaultFps} FPS
                    </span>
                </button>
                <div className="Animation_hudTooltip">Geschwindigkeit</div>
                <div id={`FpsPopup_${uniqueId}`} className="Animation_popup">
                    <div
                        className="Animation_popupOption isActive"
                        data-value="auto"
                    >
                        Auto ({activePlaybackMode.defaultFps})
                    </div>
                    {activePlaybackMode.selectableSpeeds.map((speed) => (
                        <div
                            key={speed}
                            className="Animation_popupOption"
                            data-value={speed}
                        >
                            {speed} FPS
                        </div>
                    ))}
                </div>
            </div>

            {/* Desktop Widget: Quality */}
            <div className="Animation_interactiveWrap Animation_desktopOnly">
                <button
                    id={`QualityCtrl_${uniqueId}`}
                    className="Animation_btnView"
                >
                    <Icon icon="quality" />
                </button>
                <div className="Animation_hudTooltip">Qualität</div>
                <div
                    id={`QualityPopup_${uniqueId}`}
                    className="Animation_popup"
                >
                    {qualitySelections.map((selection, index) => (
                        <div
                            key={`${selection.value}_${index}`}
                            className="Animation_popupOption"
                            data-value={selection.value}
                        >
                            {selection.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Unified Mobile Settings Trigger Button */}
            <button
                id={`SettingsMobileTrigger_${uniqueId}`}
                className="Animation_btnView Animation_mobileOnly"
            >
                <Icon icon="quality" />
            </button>

            {/* 100% Full-Screen Mobile Bottom Sheet Layout */}
            {createPortal(
                <div
                    id={`BottomSheet_${uniqueId}`}
                    className="Animation_bottomSheet"
                >
                    <div className="Animation_bottomSheetHeader">
                        <span className="Animation_bottomSheetTitle">
                            Einstellungen
                        </span>
                        <button
                            id={`BottomSheetClose_${uniqueId}`}
                            className="Animation_bottomSheetCloseBtn"
                        >
                            <Icon icon="close" />
                        </button>
                    </div>

                    {/* Tier 1 Main Submenus Panel Container */}
                    <div
                        id={`BottomSheetMainPanel_${uniqueId}`}
                        className="Animation_bottomSheetPanel isActive"
                    >
                        {hasMultipleFocusOptions && (
                            <div
                                className="Animation_bottomSheetRow"
                                data-target="focus"
                            >
                                <span>Fokus (Ausrichtung)</span>
                                <Icon icon="chevron" />
                            </div>
                        )}
                        <div
                            className="Animation_bottomSheetRow"
                            data-target="fps"
                        >
                            <span>Geschwindigkeit (FPS)</span>
                            <Icon icon="chevron" />
                        </div>
                        <div
                            className="Animation_bottomSheetRow"
                            data-target="quality"
                        >
                            <span>Qualität</span>
                            <Icon icon="chevron" />
                        </div>
                    </div>

                    {/* Tier 2 Submenu Panel: Focus Options */}
                    {hasMultipleFocusOptions && (
                        <div
                            id={`BottomSheetFocusPanel_${uniqueId}`}
                            className="Animation_bottomSheetPanel"
                        >
                            <div className="Animation_bottomSheetBackRow">
                                <Icon icon="chevron" />
                                <span>Zurück</span>
                            </div>
                            {alignmentLabels.map((label, idx) => (
                                <div
                                    key={`${label}_${idx}`}
                                    className={`Animation_bottomSheetOption ${
                                        idx === defaultAlignmentIndex
                                            ? 'isActive'
                                            : ''
                                    }`}
                                    data-type="focus"
                                    data-value={idx}
                                >
                                    {label}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Tier 2 Submenu Panel: FPS Options */}
                    <div
                        id={`BottomSheetFpsPanel_${uniqueId}`}
                        className="Animation_bottomSheetPanel"
                    >
                        <div className="Animation_bottomSheetBackRow">
                            <Icon icon="chevron" />
                            <span>Zurück</span>
                        </div>
                        <div
                            className="Animation_bottomSheetOption"
                            data-type="fps"
                            data-value="auto"
                        >
                            Auto ({activePlaybackMode.defaultFps} FPS)
                        </div>
                        {activePlaybackMode.selectableSpeeds.map((speed) => (
                            <div
                                key={speed}
                                className="Animation_bottomSheetOption"
                                data-type="fps"
                                data-value={speed}
                            >
                                {speed} FPS
                            </div>
                        ))}
                    </div>

                    {/* Tier 2 Submenu Panel: Quality Options */}
                    <div
                        id={`BottomSheetQualityPanel_${uniqueId}`}
                        className="Animation_bottomSheetPanel"
                    >
                        <div className="Animation_bottomSheetBackRow">
                            <Icon icon="chevron" />
                            <span>Zurück</span>
                        </div>
                        {qualitySelections.map((selection, index) => (
                            <div
                                key={`${selection.value}_${index}`}
                                className="Animation_bottomSheetOption"
                                data-type="quality"
                                data-value={selection.value}
                            >
                                {selection.label}
                            </div>
                        ))}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default AnimationControlSecondary;
